import axios from '@hw-core/libs/axios'
import { 
  createItem, partialUpdateItem, fetchItems, fetchItem, deleteItem, updateItem,
  buildEqualCondition, buildGreaterOrEqualCondition, buildSmallerOrEqualCondition, buildSortOption, buildLimit, buildInCondition } from '@/shared/services/collection'
import { db, functions, auth } from '@/shared/services/firebase'
import {notificationService} from '@/shared/services/notificationService'
import { DB_NAMES } from '@/shared/constants/collections'
import { GIG_STATUSES } from '@/shared/constants/gigs'
import firebase from 'firebase/app'

const buildBasicGigCondition = (accountId, isTemplate) => [buildEqualCondition('accountId', accountId), buildEqualCondition('isTemplate', isTemplate)]

const buildFetchGigsConditions = (status, accountId, onlyAreas) => {
  const conditions = buildBasicGigCondition(accountId, false)
  if (status === GIG_STATUSES.draft) {
    conditions.push(buildEqualCondition('isPublished', false))
  } else if (status === GIG_STATUSES.open) {
    conditions.push(buildEqualCondition('status', status))
    conditions.push(buildEqualCondition('isPublished', true))
  } else {
    conditions.push(buildEqualCondition('status', status))
  }

  if (onlyAreas) {
    conditions.push(buildInCondition('areaId', onlyAreas))
  }

  return conditions
}

/* const buildFetchGigsPagination = (list, currentPage, actionChangePage, limit) => {
  let pagination = {
    limit,
  }
  
  if (actionChangePage && list.length > 0) {
    pagination = {
      action: actionChangePage,
      limit,
      timeStamp: list.at(actionChangePage === 'prev' ? 0 : -1).created.toDate().getTime(),
    }
  }

  return pagination
} */

const buildFetchLocationConditions = (accountId, startcode, endcode) => [
  buildEqualCondition('accountId', accountId),
  buildGreaterOrEqualCondition('name', startcode),
  buildSmallerOrEqualCondition('name', endcode)
]

export default {
  namespaced: true,
  state: {},
  getters: {},
  mutations: {},
  actions: {
    async fetchGigs(ctx, queryParams) {
      try {
        const { accountId, onlyAreas } = queryParams

        const conditions = buildFetchGigsConditions(queryParams.status, accountId, onlyAreas)
        const sortOptions = buildSortOption('created', 'desc')

        const gigs = await fetchItems(DB_NAMES.GIGS, conditions, sortOptions)

        return { data: gigs, isMore: false }

      } catch (error) {
        return Promise.reject(error)
      }
    },
    async fetchGigTemplates(ctx, { accountId }) {
      try {
        const conditions = buildBasicGigCondition(accountId, true)
        const pagination = buildLimit(100)
        const sortOptions = buildSortOption('created', 'desc')

        const templates = await fetchItems(DB_NAMES.GIGS, conditions, sortOptions, pagination)

        return templates
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async fetchContracts(ctx, { accountId }) {
      try {
        const conditions = [buildEqualCondition('accountId', accountId)]
        const pagination = buildLimit(100)
        const sortOptions = buildSortOption('created', 'desc')

        const contracts = await fetchItems(DB_NAMES.CONTRACTS, conditions, sortOptions, pagination)

        return contracts
      } catch (error) {
        return Promise.reject(error)
      }
    },
    fetchUserShifts(ctx, { assignedTo, firstShiftStart }) {
      return new Promise((resolve, reject) => {
        db.collection('shifts')
          .where('assignedTo', '==', assignedTo)
          .where('status', 'in', ['booked', 'open', 'confirmed', 'expired'])
          .where('start', '>=', firstShiftStart)
          .orderBy('start', 'asc')
          .limit(900)
          .get()
          .then(async snapshot => {
            const data = snapshot.docs.map(doc => {
              const item = doc.data()
              return {
                ...item, id: doc.id, start: item.start.toDate(), end: item.end.toDate(),
              }
            })
            resolve(data)
          })
          .catch(() => {
            reject()
          })
      })
    },
    async fetchContract(ctx, { contractId }) {
      try {
        const contract = await fetchItem(DB_NAMES.CONTRACTS, contractId)

        return contract
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async fetchArea(ctx, { areaId }) {
      try {
        const area = await fetchItem(DB_NAMES.AREAS, areaId)

        return area
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async fetchGigShifts(ctx, { id }) {
      try {
        const conditions = [buildEqualCondition('gigId', id)]
        const sortOptions = buildSortOption('start', 'asc')

        const shifts = await fetchItems(DB_NAMES.SHIFTS, conditions, sortOptions)

        return shifts.filter(shift => shift.status !== 'deleted').map(shift => ({...shift, start: shift.start.toDate(), end: shift.end.toDate()}))
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async createGig({ dispatch }, { gig, shifts }) {
      try {
        const viewId = await dispatch('createView')
        const viewDetail = { count: 0, viewId }
        const response = await createItem(DB_NAMES.GIGS, {...gig, viewDetail})
  
        if (response) {
          const gigId = response.id

          await Promise.all(shifts.map(async s => {
            const shift = s
            const assignedTo = shift.assignedTo === '' ? shift.assignedTo : shift.assignedTo.value.id
            const {status} = shift
            shift.gigId = gigId
            shift.boosted = gig.boosted
            shift.boostedOptions = gig.boostedOptions
            shift.verificationRequest = ''
            shift.confirmationRequest = ''
            shift.title = shift.assignedTo === '' ? shift.title : `${shift.assignedTo.value.name.split(' ').shift().toLowerCase()}:${shift.title}`
            await createItem(DB_NAMES.SHIFTS, {...shift, assignedTo, status})
          }))
          const availableShifts = shifts.filter(shift => shift.status === 'open' || shift.status === 'draft').length
          await updateItem(DB_NAMES.GIGS, {gigId, availableShifts})

        }
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async editGig(ctx, { gig, shifts, processer}) {
      try {
        const saveTemplate = gig.isTemplate
        const availableShifts = shifts.filter(shift => shift.status === 'open' || shift.status === 'draft').length
        const data = {
          title: gig.title,
          location: gig.location,
          totalShifts: gig.totalShifts,
          areaId: gig.areaId,
          availableShifts,
          isPublished: gig.isPublished,
          startDate: gig.startDate,
          expDate: Number.isNaN(gig.expDate.getTime()) ? gig.startDate : gig.expDate,
          accountId: gig.accountId,
          category: gig.category,
          order: gig.order,
          tags: gig.tags,
          boosted: gig.boosted,
          boostedOptions: gig.boostedOptions,
          campaigns: gig.campaigns,
        }
        await partialUpdateItem(DB_NAMES.GIGS, gig.id, data)

        if (saveTemplate) {
          const template = {
            ...gig,
            shifts: [],
            title: '',
            totalShifts: null,
            availableShifts: null,
            status: null,
          }
          await createItem(DB_NAMES.GIGS, template)
        }

        // TODO: create a reusable function for deleting multiple items
        // Update gigs by deleting them and creating them again
        const oldShiftConditions = [buildEqualCondition('gigId', gig.id)]
        const oldShifts = await fetchItems(DB_NAMES.SHIFTS, oldShiftConditions)
        // Delete all old shifts that are not present in the new shifts
        const shiftsToBeDeleted = oldShifts.filter(shift => shifts.findIndex(s => s.id === shift.id) === -1)
        await Promise.all(shiftsToBeDeleted.map(async oldShift => {
          // If shift no longer exists in the new shifts, delete it by updating the status to deleted
          await partialUpdateItem(DB_NAMES.SHIFTS, oldShift.id,{
            status: 'deleted',
            deletedAt: new Date(),
            updates: firebase.firestore.FieldValue.arrayUnion({
              state: 'deleted',
              date: new Date(),
              processer,
              content: [
                "Vuoro poistettu",
              ]
            })
          })
        }))

        // Create new shifts that are not present in the old shifts
        const shiftsToBeCreated = shifts.filter(shift => oldShifts.findIndex(s => s.id === shift.id) === -1)
        // TODO: create a reusable for batch create items
        await Promise.all(shiftsToBeCreated.map(async s => {
          const shift = s
          shift.gigId = gig.id
          shift.verificationRequest = ''
          shift.confirmationRequest = ''
          const assignedTo = shift.assignedTo === '' ? shift.assignedTo : shift.assignedTo.value.id
          await createItem(DB_NAMES.SHIFTS, {...shift, assignedTo})
        }))

        // Update shifts that are present in both old and new shifts
        const shiftsToBeUpdated = shifts.filter(shift => oldShifts.findIndex(s => s.id === shift.id) !== -1)
        await Promise.all(shiftsToBeUpdated.map(async s => {
          const shift = s
          const assignedTo = shift.assignedTo === '' ? shift.assignedTo : shift.assignedTo.value.id
          await partialUpdateItem(DB_NAMES.SHIFTS, shift.id, {...shift, assignedTo})
        }))
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async deleteGig(ctx, { id, processer }) {
      try {
        await deleteItem(DB_NAMES.GIGS, id)

        const shiftConditions = [buildEqualCondition('gigId', id)]
        const shifts = await fetchItems(DB_NAMES.SHIFTS, shiftConditions)

        await Promise.all(shifts.map(async shift => {
          await partialUpdateItem(DB_NAMES.SHIFTS, shift.id,{
            status: 'deleted',
            deletedAt: new Date(),
            updates: firebase.firestore.FieldValue.arrayUnion({
              state: 'deleted',
              date: new Date(),
              processer,
              content: [
                "Vuoro poistettu",
              ]
            })
          })
        }))
        return Promise.resolve()
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async fetchLocation(ctx, { text, accountId }) {
      const strlength = text.length
      const strFrontCode = text.slice(0, strlength - 1)
      const strEndCode = text.slice(strlength - 1, strlength)
      const startcode = text
      const endcode = strFrontCode + String.fromCharCode(strEndCode.charCodeAt(0) + 1)
      try {
        const conditions = buildFetchLocationConditions(accountId, startcode, endcode)
        const pagination = buildLimit(6)

        const areas = await fetchItems(DB_NAMES.AREAS, conditions, null, pagination)

        return areas
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async createView() {
      try {
        const data = { viewedBy: []}
        const response = await createItem(DB_NAMES.VIEWS, data)
        return response.id
      } catch (error) {
        return Promise.reject(error)
      }
    },
    fetchInvoice(ctx, { id }) {
      return new Promise((resolve, reject) => {
        axios
          .get(`/apps/invoice/invoices/${id}`)
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    fetchLocationLatLon(ctx, { address }) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${functions.url}/locationToLatLon`, { address })
          .then(response => resolve(response))
          .catch(error => reject(error))
      })
    },
    async fetchUserViews(_ctx, id) {
      try {
        const viewItem = await fetchItem(DB_NAMES.VIEWS, id)
        if (viewItem?.viewedBy?.length > 0) {
          const users = await Promise.all(
            viewItem.viewedBy.map(async uId => {
              const user = await fetchItem(DB_NAMES.USERS, uId)
              return user
            })
          )
          return users
        }
        return []
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async createFromSuggestion(_ctx, { id, accept, activeOrderAreaNames, callback }) {
      const idToken = await auth.currentUser.getIdToken(true)
      axios
        .post(`${functions.url}/createFromSuggestion`, { orderId:id, accept, activeOrderAreaNames, token: idToken })
        .then(response => callback(response, true))
        .catch(error => callback(error, false))
    },
    fetchOrders(ctx, { callback }) {
      const twoHourAgo = new Date()
      twoHourAgo.setHours(twoHourAgo.getHours() -2)
      db.collection('orders')
        .where('created', '>=', twoHourAgo)
        .orderBy('created', 'asc')
        .onSnapshot(snapshot => {
          const changes = snapshot.docChanges()
          changes.forEach(async change => {
            const item = change.doc.data()
            item.id = change.doc.id
            if(item.status === 'accepted' || item.status === 'rejected') {
              callback({ type: 'removed', data: item })
              return
            }
            if(!item.activeOrders || item.activeOrders.length === 0) {
              return
            }
            // Get associated ticket
            const ticket = await fetchItem(DB_NAMES.TICKETS, item.tickets[0])
            item.ticketData = ticket
            // Get associated areas
            const areas = await Promise.all(item.areas.map(async areaId => {
              const area = await fetchItem(DB_NAMES.AREAS, areaId)
              return area
            }))
            // Attach area to order activeOrders
            item.activeOrders = item.activeOrders.map(order => {
              const area = areas.find(a => order?.location.toLowerCase() === a.name.toLowerCase())
              return { ...order, selectedAreaId: area ? area.id : null, selectedAreaName: area ? area.name : '', areaSuggestions: [{
                data: areas
              }], initialAreas: [{
                data: areas
              }],
              errorMessage: area ? null : `Aluetta ${  order.location  } ei löydy.`,
            }
            })
            await notificationService(`Uusi tilaus, ${item.activeOrders[0].location}`, false, 'https://hoiwa-app.web.app/apps/shifts-&-gigs/gigs-management/list')
            callback({ type: change.type, data: item })
          })
        })
    },
    async fetchThisMonthShifts() {
      const now = new Date()
      const shiftsCollection = await db
        .collection('shifts')
        .where('start', '>=', new Date(1, now.getMonth(), now.getFullYear(), 0, 0, 0))
        .get()
      
      // TODO: Get end also
      const shifts = shiftsCollection.docs.map(shift => ({
        ...shift.data(),
        id: shift.id,
      })).filter(shift => shift.status === 'open' || shift.status === 'booked' || shift.status === 'confirmed' || (shift.status === 'confirmed' || shift.status === 'expired' && shift.assignedTo !== ''))

      return shifts.length
    },
  },
}
